import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useCart } from '../../context/CartContext';
import { useTheme } from '../../context/ThemeContext';
import api from '../../services/api';

const CartSidebar = ({ isOpen, onClose }) => {
  const { cartItems: items, updateQuantity, removeFromCart, cartTotals, loading } = useCart();
  const { theme } = useTheme();
  const navigate = useNavigate();
  const totals = cartTotals || { subtotal: 0, itemsCount: 0, uniqueItems: 0 };
  const [freeShippingThreshold, setFreeShippingThreshold] = useState(3500);
  const [amountLeft, setAmountLeft] = useState(0);
  const [progressPercentage, setProgressPercentage] = useState(0);

  useEffect(() => {
    const fetchThreshold = async () => {
      try {
        const res = await api.get('/settings/announcement');
        if (res.freeShippingThreshold) {
          setFreeShippingThreshold(res.freeShippingThreshold);
        }
      } catch (err) {
        console.error('Failed to fetch threshold');
      }
    };
    fetchThreshold();
  }, []);

  useEffect(() => {
    const subtotal = totals.subtotal || 0;
    const left = Math.max(0, freeShippingThreshold - subtotal);
    const progress = Math.min((subtotal / freeShippingThreshold) * 100, 100);
    setAmountLeft(left);
    setProgressPercentage(progress);
  }, [totals.subtotal, freeShippingThreshold]);

  const primaryColor = theme.primaryColor;
  const secondaryColor = theme.secondaryColor;
  const textColor = theme.textColor;
  const borderColor = theme.borderColor || '#e5e5e5';
  const backgroundColor = theme.backgroundColor || '#ffffff';
  const mutedTextColor = theme.mutedText || '#888888';

  return (
    <>
      <div className={`cart-sidebar-overlay ${isOpen ? 'active' : ''}`} onClick={onClose} />
      <div className={`cart-sidebar ${isOpen ? 'active' : ''}`} style={{ backgroundColor }}>
        <div className="cart-header" style={{ borderBottomColor: borderColor }}>
          <div className="cart-header-content">
            <h2 className="cart-title" style={{ color: textColor }}>
              Cart {loading && <span className="text-sm text-gray-400 ml-2 animate-pulse">Syncing...</span>}
            </h2>
            <div className="flex items-center gap-4">
              {items.length > 0 && (
                <Link 
                  to="/cart" 
                  onClick={onClose}
                  className="text-sm font-medium hover:underline"
                  style={{ color: primaryColor }}
                >
                  View Cart
                </Link>
              )}
              <button className="cart-close" onClick={onClose} style={{ borderColor, color: textColor }}>
                ×
              </button>
            </div>
          </div>
          <div className="cart-header-underline" style={{ backgroundColor: secondaryColor }} />
        </div>

        <div className="free-shipping" style={{ backgroundColor: borderColor }}>
          {totals.subtotal >= freeShippingThreshold ? (
            <p className="free-shipping-text" style={{ color: textColor }}>
              <span style={{ color: primaryColor }}>Congratulations!</span> Free shipping!
            </p>
          ) : (
            <p className="free-shipping-text" style={{ color: textColor }}>
              You're only <span style={{ color: primaryColor }}>Rs. {amountLeft.toLocaleString()}</span> away from free shipping!
            </p>
          )}
          <div className="shipping-progress-bar">
            <div className="shipping-progress-fill" style={{ width: `${progressPercentage}%`, backgroundColor: primaryColor }} />
          </div>
        </div>

        <div className="cart-items-container hide-scrollbar">
          {items.length === 0 ? (
            <div className="empty-cart-sidebar" style={{ textAlign: 'center', marginTop: '60px' }}>
              <p style={{ color: textColor, marginBottom: '20px' }}>Your cart is empty</p>
              <button
                onClick={() => { onClose(); navigate('/'); }}
                className="px-6 py-2 rounded text-white font-medium hover:opacity-90 transition-opacity"
                style={{ backgroundColor: primaryColor }}
              >
                Shop Now
              </button>
            </div>
          ) : (
            items.map(item => (
              <div key={item.id} className="cart-sidebar-item" style={{ borderBottomColor: borderColor }}>
                <img src={item.image} alt={item.name} className="cart-item-image" />
                <div className="cart-item-details">
                  <h4 style={{ color: textColor }}>{item.name}</h4>
                  {item.options && (
                    <div className="cart-item-variants" style={{ color: mutedTextColor }}>
                      {item.options.color && <span>Color: {item.options.color}</span>}
                      {item.options.size && <span> Size: {item.options.size}</span>}
                    </div>
                  )}
                  <div className="cart-item-actions">
                    <div className="quantity-controls" style={{ borderColor }}>
                      <button 
                        onClick={() => updateQuantity(item.id, item.quantity - 1)} 
                        disabled={loading || item.quantity <= 1}
                        style={{ color: textColor }}
                      >
                        −
                      </button>
                      <span style={{ color: textColor }}>{item.quantity}</span>
                      <button 
                        onClick={() => updateQuantity(item.id, item.quantity + 1)} 
                        disabled={loading}
                        style={{ color: textColor }}
                      >
                        +
                      </button>
                    </div>
                    <span className="cart-item-price" style={{ color: primaryColor }}>Rs. {(item.price * item.quantity).toLocaleString()}</span>
                    <button 
                      className="remove-item" 
                      onClick={() => removeFromCart(item.id)} 
                      disabled={loading}
                      style={{ color: mutedTextColor }}
                    >
                      Remove
                    </button>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>

        {items.length > 0 && (
          <div className="cart-footer" style={{ borderTopColor: borderColor, backgroundColor }}>
            <div className="cart-total">
              <span style={{ color: textColor }}>Subtotal:</span>
              <span className="cart-total-amount" style={{ color: primaryColor }}>Rs. {totals.subtotal.toLocaleString()}</span>
            </div>
            <button
              onClick={() => { onClose(); navigate('/checkout'); }}
              className="checkout-btn"
              style={{ backgroundColor: primaryColor, color: '#fff' }}
            >
              Checkout
            </button>
          </div>
        )}
      </div>

      {/* Inline styles – all colors now use theme variables */}
      <style>{`
        .cart-sidebar .quantity-controls {
          display: inline-flex;
          align-items: center;
          border: 1px solid ${borderColor};
          border-radius: 6px;
          overflow: hidden;
          background: ${backgroundColor};
          height: 32px;
        }
        .cart-sidebar .quantity-controls button {
          width: 30px;
          height: 100%;
          display: flex;
          align-items: center;
          justify-content: center;
          background: transparent;
          border: none;
          font-size: 16px;
          font-weight: 400;
          cursor: pointer;
          transition: background 0.15s ease;
          color: ${textColor};
          padding: 0;
          line-height: 1;
        }
        .cart-sidebar .quantity-controls button:hover:not(:disabled) {
          background: ${borderColor};
        }
        .cart-sidebar .quantity-controls button:disabled {
          opacity: 0.4;
          cursor: not-allowed;
        }
        .cart-sidebar .quantity-controls span {
          min-width: 28px;
          text-align: center;
          font-size: 14px;
          font-weight: 500;
          color: ${textColor};
          padding: 0 4px;
          line-height: 30px;
          border-left: 1px solid ${borderColor};
          border-right: 1px solid ${borderColor};
        }
        .cart-sidebar .cart-item-actions {
          display: flex;
          align-items: center;
          gap: 10px;
          flex-wrap: wrap;
          margin-top: 6px;
        }
        .cart-sidebar .cart-item-price {
          font-size: 14px;
          font-weight: 500;
          color: ${primaryColor};
          white-space: nowrap;
        }
        .cart-sidebar .remove-item {
          background: none;
          border: none;
          font-size: 12px;
          font-weight: 400;
          text-decoration: underline;
          cursor: pointer;
          padding: 2px 4px;
          transition: color 0.15s ease;
          color: ${mutedTextColor};
        }
        .cart-sidebar .remove-item:hover:not(:disabled) {
          color: ${textColor};
        }
        .cart-sidebar .remove-item:disabled {
          opacity: 0.4;
          cursor: not-allowed;
        }
        .cart-sidebar .cart-item-details {
          flex: 1;
        }
        .cart-sidebar .cart-item-details h4 {
          font-size: 14px;
          font-weight: 500;
          margin: 0 0 2px 0;
          color: ${textColor};
        }
        .cart-sidebar .cart-item-details .cart-item-variants {
          font-size: 12px;
          color: ${mutedTextColor};
          margin-bottom: 4px;
        }
        .cart-sidebar .cart-item-image {
          width: 56px;
          height: 72px;
          object-fit: cover;
          border-radius: 4px;
          flex-shrink: 0;
          background: ${borderColor};
        }
        .cart-sidebar .cart-sidebar-item {
          display: flex;
          gap: 12px;
          padding: 14px 0;
          border-bottom: 1px solid ${borderColor};
        }
        .cart-sidebar .cart-sidebar-item:last-child {
          border-bottom: none;
        }
        .cart-sidebar .hide-scrollbar::-webkit-scrollbar {
          display: none;
        }
        .cart-sidebar .hide-scrollbar {
          -ms-overflow-style: none;
          scrollbar-width: none;
        }
        .cart-sidebar .cart-items-container {
          flex: 1;
          overflow-y: auto;
          padding: 0 20px 8px 20px;
        }
        .cart-sidebar .cart-footer {
          padding: 16px 20px 20px 20px;
          border-top: 1px solid ${borderColor};
          background: ${backgroundColor};
        }
        .cart-sidebar .checkout-btn {
          width: 100%;
          padding: 12px;
          border: none;
          border-radius: 6px;
          font-size: 14px;
          font-weight: 600;
          cursor: pointer;
          transition: opacity 0.15s ease;
          color: #fff;
          background: ${primaryColor};
        }
        .cart-sidebar .checkout-btn:hover {
          opacity: 0.85;
        }
        .cart-sidebar .cart-total {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 12px;
          font-size: 16px;
          font-weight: 500;
          color: ${textColor};
        }
        .cart-sidebar .cart-total-amount {
          font-weight: 600;
          color: ${primaryColor};
        }
        .cart-sidebar .free-shipping {
          padding: 12px 20px;
          margin: 0 20px 12px 20px;
          border-radius: 8px;
          background: ${borderColor};
        }
        .cart-sidebar .free-shipping-text {
          font-size: 13px;
          color: ${textColor};
          margin-bottom: 6px;
          text-align: center;
        }
        .cart-sidebar .free-shipping-text span {
          font-weight: 600;
          color: ${primaryColor};
        }
        .cart-sidebar .shipping-progress-bar {
          height: 4px;
          background: rgba(0,0,0,0.08);
          border-radius: 4px;
          overflow: hidden;
        }
        .cart-sidebar .shipping-progress-fill {
          height: 100%;
          border-radius: 4px;
          transition: width 0.3s ease;
          background: ${primaryColor};
        }
        .cart-sidebar .cart-header {
          padding: 16px 20px 12px 20px;
          border-bottom: 1px solid ${borderColor};
        }
        .cart-sidebar .cart-header-content {
          display: flex;
          justify-content: space-between;
          align-items: center;
        }
        .cart-sidebar .cart-title {
          font-size: 20px;
          font-weight: 300;
          color: ${textColor};
          margin: 0;
        }
        .cart-sidebar .cart-close {
          width: 32px;
          height: 32px;
          border: 1px solid ${borderColor};
          background: transparent;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 20px;
          cursor: pointer;
          color: ${textColor};
          transition: background 0.15s ease;
        }
        .cart-sidebar .cart-close:hover {
          background: ${borderColor};
        }
        .cart-sidebar .cart-header-underline {
          height: 2px;
          width: 100%;
          margin-top: 10px;
          background: ${secondaryColor};
        }
        .cart-sidebar {
          position: fixed;
          top: 0;
          right: -420px;
          width: 100%;
          max-width: 400px;
          height: 100vh;
          background: ${backgroundColor};
          z-index: 2001;
          transition: right 0.3s ease;
          display: flex;
          flex-direction: column;
          box-shadow: -4px 0 20px rgba(0,0,0,0.08);
        }
        .cart-sidebar.active {
          right: 0;
        }
        .cart-sidebar-overlay {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: rgba(0,0,0,0.4);
          z-index: 2000;
          opacity: 0;
          visibility: hidden;
          transition: all 0.3s ease;
        }
        .cart-sidebar-overlay.active {
          opacity: 1;
          visibility: visible;
        }
        .cart-sidebar .empty-cart-sidebar p {
          font-size: 16px;
          color: ${textColor};
        }
      `}</style>
    </>
  );
};

export default CartSidebar;